import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Switch,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Restore,
  Share,
  People,
  EmojiEvents,
  PersonAdd,
} from '@mui/icons-material';
import {
  useTeam,
  useUpdateTeam,
  useDeleteTeam,
  useRestoreTeam,
  usePlayers,
  useCreatePlayer,
  useTournaments,
} from '@/hooks/useApi';
import { LoadingState, ErrorState, ResponsiveDialog } from '@/components';
import { KebabMenu, shareLink } from '@/components/manage';
import { useAdminMode } from '@/hooks/useManage';
import { useNotification } from '@/contexts/NotificationContext';
import { PlayerGender } from '@/api/generated/model';
import { getApiErrorMessage } from '@/utils/apiError';

const getSportIcon = (sportType: string): string => {
  const icons: Record<string, string> = {
    football: '⚽', hockey: '🏒', basketball: '🏀', tennis: '🎾',
    volleyball: '🏐', handball: '🤾', padel: '🎾',
    swimming: '🏊', poker: '🃏', other: '🏆',
  };
  return icons[sportType] || '🏆';
};

const getSportColor = (sportType: string): string => {
  const colors: Record<string, string> = {
    football: '#4caf50', hockey: '#2196f3', basketball: '#ff9800', tennis: '#e91e63',
    volleyball: '#9c27b0', handball: '#00bcd4', padel: '#8bc34a',
    swimming: '#03a9f4', poker: '#ff5722', other: '#757575',
  };
  return colors[sportType] || '#757575';
};

export const TeamDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get('returnTo');
  const [activeTab, setActiveTab] = useState(0);
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false);
  const [addPlayerMode, setAddPlayerMode] = useState<'existing' | 'new'>('existing');
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [newPlayerFirstName, setNewPlayerFirstName] = useState('');
  const [newPlayerLastName, setNewPlayerLastName] = useState('');
  const [newPlayerGender, setNewPlayerGender] = useState<PlayerGender | ''>('');
  const [newPlayerBirthDate, setNewPlayerBirthDate] = useState('');
  const [newPlayerActive, setNewPlayerActive] = useState(true);
  const [addPlayerError, setAddPlayerError] = useState('');

  const { data: team, isLoading, error, refetch } = useTeam(id || '');
  const { data: allPlayers } = usePlayers({ limit: 100 });
  const { data: tournaments } = useTournaments({ limit: 20 });
  const updateTeam = useUpdateTeam();
  const createPlayer = useCreatePlayer();
  const deleteTeam = useDeleteTeam();
  const restoreTeam = useRestoreTeam();
  const { can } = useAdminMode();
  const { showSuccess } = useNotification();

  const handleRestore = async () => {
    await restoreTeam.mutateAsync(id!);
    refetch();
  };

  const handleAddPlayer = async () => {
    setAddPlayerError('');
    try {
      let playerIdToAdd = selectedPlayerId;

      if (addPlayerMode === 'new') {
        const firstName = newPlayerFirstName.trim();
        if (!firstName) {
          setAddPlayerError('Укажите имя игрока');
          return;
        }
        const lastName = newPlayerLastName.trim();
        const createdPlayer = await createPlayer.mutateAsync({
          firstName,
          lastName: lastName || null,
          gender: newPlayerGender || null,
          birthDate: newPlayerBirthDate || null,
          active: newPlayerActive,
        });
        playerIdToAdd = createdPlayer.id;
      } else if (!playerIdToAdd) {
        setAddPlayerError('Выберите игрока из списка');
        return;
      }

      const currentRoster = team?.roster?.map((p) => p.id) || [];
      const nextRoster = currentRoster.includes(playerIdToAdd)
        ? currentRoster
        : [...currentRoster, playerIdToAdd];

      await updateTeam.mutateAsync({
        id: id!,
        data: { roster: nextRoster },
      });

      setShowAddPlayerDialog(false);
      setAddPlayerMode('existing');
      setSelectedPlayerId('');
      setNewPlayerFirstName('');
      setNewPlayerLastName('');
      setNewPlayerGender('');
      setNewPlayerBirthDate('');
      setNewPlayerActive(true);
      refetch();
    } catch (error) {
      setAddPlayerError(getApiErrorMessage(error, 'Не удалось добавить игрока в состав.'));
    }
  };

  const availablePlayers = allPlayers?.data.filter(
    p => !team?.roster?.some(rp => rp.id === p.id)
  );

  if (isLoading) return <LoadingState fullscreen message="Загрузка команды..." />;
  if (error || !team) return (
    <Box sx={{ p: 2 }}>
      <IconButton onClick={() => navigate(-1)} sx={{ mb: 2 }}><ArrowBack /></IconButton>
      <ErrorState message="Ошибка загрузки команды" onRetry={refetch} />
    </Box>
  );

  return (
    <Box sx={{ pb: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <IconButton onClick={() => navigate(-1)}><ArrowBack /></IconButton>
        <Avatar sx={{ bgcolor: getSportColor(team.sportType), width: 56, height: 56, fontSize: '2rem' }}>
          {getSportIcon(team.sportType)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight="bold">{team.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip label={team.sportType} size="small" />
            {team.cityName && <Chip label={team.cityName} size="small" />}
            {!team.active && <Chip label="Неактивна" size="small" color="default" />}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {returnTo && (
            <Button size="small" variant="outlined" onClick={() => navigate(returnTo)}>К турниру</Button>
          )}
          <KebabMenu
            items={[
              {
                id: 'share',
                label: 'Поделиться',
                icon: <Share fontSize="small" />,
                onClick: async () => {
                  try {
                    if ((await shareLink(team.name)) === 'copied') showSuccess('Ссылка скопирована');
                  } catch {
                    /* отмена системного меню */
                  }
                },
              },
              {
                id: 'edit',
                label: 'Изменить',
                icon: <Edit fontSize="small" />,
                hidden: !can(team.permissions?.update),
                onClick: () => navigate(`/teams/${id}/edit`),
              },
              {
                id: 'restore',
                label: 'Сделать активной',
                icon: <Restore fontSize="small" />,
                hidden: team.active || !can(team.permissions?.update),
                onClick: handleRestore,
              },
              {
                id: 'delete',
                label: 'Удалить',
                icon: <Delete fontSize="small" />,
                danger: true,
                hidden: !can(team.permissions?.delete),
                confirm: { title: 'Удалить команду?' },
                onClick: async () => {
                  await deleteTeam.mutateAsync(id!);
                  navigate('/teams', { replace: true });
                },
              },
            ]}
          />
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth">
          <Tab icon={<People />} label="Состав" />
          <Tab icon={<EmojiEvents />} label="Турниры" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ p: 2 }}>
        {activeTab === 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Игроки ({team.roster?.length || 0})</Typography>
              {can(team.permissions?.update) && (
                <Button variant="contained" color="manage" startIcon={<PersonAdd />} onClick={() => setShowAddPlayerDialog(true)}>Добавить</Button>
              )}
            </Box>
            {(!team.roster || team.roster.length === 0) && (
              <Alert severity="info" sx={{ mb: 2 }}>В составе пока нет игроков.</Alert>
            )}
            <List>
              {team.roster?.map((player) => (
                <Paper key={player.id} sx={{ mb: 1 }}>
                  <ListItem onClick={() => navigate(`/players/${player.id}`)} sx={{ cursor: 'pointer', minHeight: 56 }}>
                    <ListItemAvatar>
                      <Avatar>{player.firstName.charAt(0)}{player.lastName?.charAt(0)}</Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={`${player.firstName} ${player.lastName || ''}`} />
                  </ListItem>
                </Paper>
              ))}
            </List>
          </>
        )}

        {activeTab === 1 && (
          <>
            <Typography variant="h6" gutterBottom>Турниры команды</Typography>
            {tournaments?.data.filter(t => t.participants?.some(p => p.id === id)).map((tournament) => (
              <Paper key={tournament.id} sx={{ p: 2, mb: 1 }} onClick={() => navigate(`/tournaments/${tournament.id}`)}>
                <Typography fontWeight="medium">{tournament.name}</Typography>
                <Chip label={tournament.status} size="small" sx={{ mt: 1 }} />
              </Paper>
            ))}
          </>
        )}
      </Box>

      {/* Add Player Dialog */}
      <ResponsiveDialog
        open={showAddPlayerDialog}
        onClose={() => setShowAddPlayerDialog(false)}
        title="Добавить игрока"
        actions={
          <>
            <Button
              onClick={() => {
                setShowAddPlayerDialog(false);
                setAddPlayerMode('existing');
                setSelectedPlayerId('');
                setNewPlayerFirstName('');
                setNewPlayerLastName('');
                setNewPlayerGender('');
                setNewPlayerBirthDate('');
                setNewPlayerActive(true);
                setAddPlayerError('');
              }}
              sx={{ minHeight: 48 }}
            >
              Отмена
            </Button>
            <Button
              onClick={handleAddPlayer}
              variant="contained"
              disabled={
                updateTeam.isPending ||
                createPlayer.isPending ||
                (addPlayerMode === 'existing' && !selectedPlayerId) ||
                (addPlayerMode === 'new' && !newPlayerFirstName.trim())
              }
              sx={{ minHeight: 48 }}
            >
              {updateTeam.isPending || createPlayer.isPending ? 'Сохранение...' : 'Добавить'}
            </Button>
          </>
        }
      >
          <Tabs
            value={addPlayerMode}
            onChange={(_, value) => {
              setAddPlayerMode(value);
              setAddPlayerError('');
            }}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="existing" label="Найти существующего" />
            <Tab value="new" label="Создать нового" />
          </Tabs>

          {addPlayerError && <Alert severity="error" sx={{ mt: 2 }}>{addPlayerError}</Alert>}

          {addPlayerMode === 'existing' ? (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Игрок</InputLabel>
              <Select value={selectedPlayerId} onChange={(e) => setSelectedPlayerId(e.target.value)}>
                {availablePlayers?.map((player) => (
                  <MenuItem key={player.id} value={player.id}>
                    {player.firstName} {player.lastName || ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Имя"
                value={newPlayerFirstName}
                onChange={(e) => setNewPlayerFirstName(e.target.value)}
                required
                fullWidth
              />
              <TextField
                label="Фамилия"
                value={newPlayerLastName}
                onChange={(e) => setNewPlayerLastName(e.target.value)}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Пол</InputLabel>
                <Select
                  value={newPlayerGender}
                  label="Пол"
                  onChange={(e) => setNewPlayerGender(e.target.value as PlayerGender | '')}
                >
                  <MenuItem value=""><em>Не указан</em></MenuItem>
                  <MenuItem value="male">Мужской</MenuItem>
                  <MenuItem value="female">Женский</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Дата рождения"
                type="date"
                value={newPlayerBirthDate}
                onChange={(e) => setNewPlayerBirthDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={newPlayerActive}
                    onChange={(e) => setNewPlayerActive(e.target.checked)}
                  />
                }
                label="Активен"
              />
            </Box>
          )}
      </ResponsiveDialog>
    </Box>
  );
};
